import React, { useCallback, useRef } from 'react';
import noop from 'lodash/noop';
import { AsyncBulkOpsCore } from '@atlassian/jira-bulk-operations-async-core';
import type { ReferenceLoader, IssuesUpdatePayload } from '@atlassian/jira-bulk-operations-common';
import { ff } from '@atlassian/jira-feature-flagging';
import { useCloseIssueViewOnBulkOpsEdit } from '../../services/use-close-issue-view';
import { useBacklogDispatch, useBacklogSelector } from '../../state';
import {
	cardsBulkOperationsFailure,
	cardsBulkOperationsRequest,
	cardsBulkOperationsRequests,
	cardsBulkOperationsSuccess,
	type CardsBulkOperationsPayload,
} from '../../state/actions/cards/bulk-operations';
import { getIsCompanyManaged } from '../../state/selectors/app';
import { getCards, getSelectedCards, getVisibleCardIds } from '../../state/selectors/cards';
import { getEstimationFieldId } from '../../state/selectors/estimation';
import { canBulkEditIssues } from '../../state/selectors/permissions';
import { getBulkOperationsData, getBulkOperationsDataOld } from '../bulk-operations-sidebar/utils';
import { useSelectActions } from './select-actions';

type BulkOperationsLoaderProps = {
	bulkOperationsToolbar: ReferenceLoader;
	bulkOperationsForm: ReferenceLoader;
	onOpenSidebar: () => void;
	onCloseSidebar: () => void;
};

export const useSelectedCards = () => useBacklogSelector(getSelectedCards);
export const useHasBulkEditPermission = () => useBacklogSelector(canBulkEditIssues);
export const useVisibleCardIds = () => useBacklogSelector(getVisibleCardIds);
export const useEstimationFieldId = () => useBacklogSelector(getEstimationFieldId);
export const useIsCMP = () => useBacklogSelector(getIsCompanyManaged);

export const BulkOperationsLoader = ({
	bulkOperationsToolbar,
	bulkOperationsForm,
	onOpenSidebar,
	onCloseSidebar,
}: BulkOperationsLoaderProps) => {
	const selectedCardIds = useSelectedCards();
	const hasBulkEditPermission = useHasBulkEditPermission();
	const cardEntities = useBacklogSelector(getCards);

	const visibleCardIds = useVisibleCardIds();
	const { selectAllVisible, clearAll, clearAllVisible, extraAnalyticsAttributes } =
		useSelectActions();

	const dispatch = useBacklogDispatch();
	const estimationFieldId = useEstimationFieldId();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	const isCMP = ff('bulk-operations-transitions-in-backlog_1e7ke') ? useIsCMP() : false;

	const { openIssueView, closeIssueView } = useCloseIssueViewOnBulkOpsEdit();

	const optimisticId = useRef<string | undefined>();

	const onUpdateStartOld = useCallback(
		({ data, issueIds }: IssuesUpdatePayload) => {
			const cardsData = ff('bulk-operations-transitions-in-backlog_1e7ke')
				? getBulkOperationsData({ data, estimationFieldId, isCMP })
				: getBulkOperationsDataOld(data, estimationFieldId);
			const action = cardsBulkOperationsRequest({ cardIds: issueIds, data: cardsData });
			optimisticId.current = action.meta.optimistic.id;
			dispatch(action);
		},
		[dispatch, estimationFieldId, isCMP],
	);

	const onUpdateStart = ff('bulk-transition-multiple-workflows_wtnvv')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useCallback(
				(payload: IssuesUpdatePayload[]) => {
					const cardsData: CardsBulkOperationsPayload[] = payload.map(({ issueIds, data }) => {
						const issuesData = getBulkOperationsData({
							data,
							estimationFieldId,
							isCMP,
						});
						return {
							cardIds: issueIds,
							data: issuesData,
						};
					});
					const action = cardsBulkOperationsRequests(cardsData);
					optimisticId.current = action.meta.optimistic.id;
					dispatch(action);
				},
				[dispatch, estimationFieldId, isCMP],
			)
		: noop;

	/*
    This function is a temporary workaround to open the legacy bulk edit page.
    The Bulk Transition doesn't have support to return the issueKeys so this implementation was done here
    and will be removed when we implement Bulk Transition with multiple workflows.
    Project Plan: https://hello.atlassian.net/wiki/spaces/JSDee/pages/3388755384/Bulk+Transition+Project+Plan
    */
	const onLegacyBulkEditRequest = useCallback(
		(issueIds: number[]) => {
			if (!ff('bulk-operations-transitions-in-backlog_1e7ke')) {
				return;
			}

			const issueKeys = issueIds.map((id) => cardEntities[String(id)]?.key);
			const url = `/secure/GHBulkChange.jspa?issueKeys=${encodeURIComponent(issueKeys.join(','))}`;

			window.open(url, '_blank');
		},
		[cardEntities],
	);

	const onUpdateError = useCallback(
		(cardIds: number[]) => {
			if (!optimisticId.current) {
				return;
			}
			dispatch(cardsBulkOperationsFailure(optimisticId.current, cardIds));
		},
		[dispatch],
	);

	const onUpdateSuccess = useCallback(
		(cardIds: number[]) => {
			if (!optimisticId.current) {
				return;
			}
			dispatch(cardsBulkOperationsSuccess(optimisticId.current, cardIds));
		},
		[dispatch],
	);

	const onOpenForm = useCallback(() => {
		onOpenSidebar();
		closeIssueView();
	}, [closeIssueView, onOpenSidebar]);

	const onCloseForm = useCallback(() => {
		onCloseSidebar();
		openIssueView();
	}, [openIssueView, onCloseSidebar]);

	return (
		<AsyncBulkOpsCore
			bulkOperationsToolbar={bulkOperationsToolbar}
			bulkOperationsForm={bulkOperationsForm}
			onOpenForm={onOpenForm}
			onCloseForm={onCloseForm}
			onUpdateStartOld={onUpdateStartOld}
			onUpdateError={onUpdateError}
			onUpdateSuccess={onUpdateSuccess}
			onLegacyBulkEditRequest={onLegacyBulkEditRequest}
			onSelectAll={selectAllVisible}
			selectableIssueIds={visibleCardIds}
			selectedIssueIds={selectedCardIds}
			extraAnalyticsAttributes={extraAnalyticsAttributes}
			onClearAll={clearAll}
			onClearAllVisible={clearAllVisible}
			hasBulkEditPermission={hasBulkEditPermission}
			{...(ff('bulk-transition-multiple-workflows_wtnvv')
				? {
						onUpdateStart,
					}
				: {})}
		/>
	);
};
